/* eslint-disable no-use-before-define */
/* eslint-disable no-param-reassign */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Row } from '@cochlear-design-system/foundation';
import { Feature } from 'flagged';

/** Utils */
import { getLabelsForFields } from '../../utils/labelHelper';
import { getMergedAddress } from '../../utils/addressHelper';
import { setIdpParam } from '../../utils/authprovider';
import { getAge } from '../../utils/dateTimeFns';
import { getSelectedRecipientCId } from '../../utils/mcpcid';
import getQueryString from '../../utils/queryStrings';
import { replaceTokens } from '../../utils/stringFns';
import { shouldUseAmplify } from '../../utils/apiTarget';
import { shouldRedirectForClinicSync } from '../../utils/mcpClinicSync';
import ClinicSyncFrame from '../ClinicSyncFrame';
import {
  getDobString,
  getFullName,
  hasExistingDevices,
  createAlmRequestBody,
} from './utils/patientRecordUtils';
import {
  filterSoundProcessorsSummary,
  filterUnknownDataSummary,
  filterSoundProcessor as createFilterSoundProcessor,
  filterImplants as createFilterImplants,
  filterAccessories as createFilterAccessories,
  resetPreviousExpandedSections,
} from './utils/filters';

/** Redux/Context */
import error from '../../actions/errorAction';
import { PatientRecordContext } from './contexts/PatientRecordContext';

/** Services */
import cookieService from '../../services/cookieService';
import patientService from '../../services/patientService';
import providerService from '../../services/providerService';
import carerService from '../../services/carerService';

/** Modals */
import { FindClinicModal } from '../../ui/FindClinicModal/FindClinicModal';
import { AuthorizationAssociateClinicModal } from './modals/AuthorizationAssociateClinicModal';
import { ViewServiceRequestModal } from './modals/ViewServiceRequestModal';
import { CreateServiceRequestModal } from './modals/CreateServiceRequestModal';
import { EndOfServiceModal } from './modals/EndOfServiceModal';
import { ReportIssueModal } from './modals/ReportIssueModal';
import AccountInviteModal from '../AccountInviteModal';
import UpdateAccessoryModal from '../UpdateAccessoryModal';

/** Components */
import { TopHeading } from './components/TopHeading';
import { NotiAlert } from './components/NotiAlert';
import AssociationSpinner from '../AssociationSpinner';
import { DeceasedView } from './components/DeceasedView';
import { LeftPanel } from './components/LeftPanel';
import { RightPanel } from './components/RightPanel';
import DisabledFeature from '../DisabledFeature';

/** Configs */
import {
  getPatientDeceasedConfig,
  getNotiAlertConfig,
  genericEmptyListElementsConfig,
  genericErrorElementsConfig,
} from './configs/patientRecordConfig';
import {
  getLeftPanelConfig,
  errorModeConfig,
} from './configs/leftPanelConfig';
import { getRightPanelConfig } from './configs/rightPanelConfig';
import { getFindClinicModalConfig } from '../../cdsConfigs/findClinicModalConfig';
import { routes, ACCESS_TOKEN_COOKIE } from '../../config';
import {
  formatPatientDetailsForSR,
  formatClinicDetailsForSR,
  formatDeviceDataForSR,
} from './utils/serviceRequestFormatters';
import { computeSmartSyncScenario } from './utils/smartSyncUtils';

/** Mappers */
import {
  formatExpandedData,
  formatPatientCarersData,
  formatPatientClinicData,
  formatPatientDeviceData,
  getGenericEmptyListData,
  getGenericErrorData,
  patientData,
  getDeviceRegInProgressLabel,
} from './mappers/dataMapper';
import {
  panelConfigMapper,
  getCarerReadElementsConfig,
} from './mappers/configMapper';
import ReturnLoanerModal from '../ClinicEquipment/components/ReturnLoanerModal';
import { returnLoaner } from '../../services/providerDeviceService';

/** Helper Functions */
const getUpdatedLeftPanelConfig = (existingConfig, viewId, cb) => {
  const updatedConfig = { ...existingConfig };
  const viewIndex = updatedConfig.views.findIndex(
    ({ id }) => id === viewId,
  );
  updatedConfig.views[viewIndex] = cb(updatedConfig.views[viewIndex]);
  return updatedConfig;
};

const injectViewErrorConfig = (view) => {
  const updatedView = {
    ...view,
    isLoading: false,
  };
  updatedView.read.fieldLayout = 'horizontal';
  updatedView.read.elements = genericErrorElementsConfig;
  return updatedView;
};

const PatientRecord = ({ rendering, country, lang }) => {
  /** Hooks & Selectors */
  const labels = getLabelsForFields(rendering.fields);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const notifications = useSelector(
    (state) => state.notificationStatus.notifications,
  );
  const professional = useSelector((state) => state.userDetails);

  /** Constants & Derived values */
  const { providerId } = providerService.getProviderDetails();
  const patientId = getQueryString('patientId');
  const { isDistributor, countryCode: providerCountryCode } =
    providerService.getCurrentProvider();
  const locationState = useState(location.state)[0];
  const isDeceasedState = locationState?.isDeceased || false;
  const optimisticCarerEnabled =
    localStorage.getItem('INSTANT') === 'true';
  const optimisticCarerRef = useRef(
    (() => {
      if (!optimisticCarerEnabled) return null;
      if (locationState?.optimisticCarer) {
        return locationState.optimisticCarer;
      }
      try {
        const stored = sessionStorage.getItem(
          `dpx_optimisticCarer_${patientId}`,
        );
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    })(),
  );
  const userCountry = country.toUpperCase();
  const { professionalId } = professional;
  const selectedRecipientCId = getSelectedRecipientCId(patientId);
  const deviceRegInProgress = cookieService.getCookie(
    'deviceRegInProgress',
  );
  const deviceRegInProgressLabel = getDeviceRegInProgressLabel(
    deviceRegInProgress,
  );
  const providerRegion = providerService.getCurrentProviderRegion();

  // Get the current provider to check for the useNewSRPortal flag
  const currentProvider = providerService.getCurrentProvider();

  const useModuleFederationSR = shouldUseAmplify(
    currentProvider?.useNewSRPortal ?? false,
  );

  const arrayTokens = [
    {
      key: `##___CLINICCOCHLEARID___##`,
      value: providerId,
    },
    {
      key: `##___RECIPIENTCOCHLEARID___##`,
      value: patientId,
    },
  ];

  const cacheControlHeader = {
    'cache-control': 'no-cache',
  };

  /** Patient Data State */
  const [patientDetails, setPatientDetails] = useState({});
  const [rawPatientDetails, setRawPatientDetails] = useState({});
  const [carersData, setCarersData] = useState();
  const [, setRawCarersDetails] = useState();
  const [associatedClinicData, setAssociatedClinicData] = useState(
    {},
  );

  /** Equipment Data State */
  const [originalEquipmentData, setOriginalEquipmentData] = useState(
    [],
  );
  const [equipmentData, setEquipmentData] = useState([]);
  const [rawEquipmentList, setRawEquipmentList] = useState(null);
  const equipmentDataRef = useRef([]);
  const rawEquipmentListRef = useRef(null);
  const patientDetailsRef = useRef({});
  const associatedClinicDataRef = useRef({});
  const expandedDataCacheRef = useRef(new Map());

  const [showAssociationSpinner, setShowAssociationSpinner] =
    useState(false);
  const isPollActiveRef = useRef(false);
  const navigationPollRef = useRef(null);

  /** Modal State */
  const [
    showSelectSurgeryCentreModal,
    setShowSelectSurgeryCentreModal,
  ] = useState(false);
  const [
    showAuthorizationAssociateClinicModal,
    setShowAuthorizationAssociateClinicModal,
  ] = useState(false);
  const [
    showCreateServiceRequestModal,
    setShowCreateServiceRequestModal,
  ] = useState(false);
  const [showEndOfServiceModal, setShowEndOfServiceModal] =
    useState(false);
  const [
    showViewServiceRequestModal,
    setShowViewServiceRequestModal,
  ] = useState(false);
  // Deferred SR-flow action that runs once MCP's active clinic has been synced
  // in place (see ClinicSyncFrame)
  const [pendingClinicSyncAction, setPendingClinicSyncAction] =
    useState(null);
  const [showReportIssueModal, setShowReportIssueModal] =
    useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showUpdateAccessoryModal, setShowUpdateAccessoryModal] =
    useState(false);
  const [showReturnLoanerModal, setShowReturnLoanerModal] =
    useState(false);
  const [returnLoanerSubmitting, setReturnLoanerSubmitting] =
    useState(false);

  /** Service Request State */
  const [deviceId, setDeviceId] = useState();
  const [createServiceRequestUrl, setCreateServiceRequestUrl] =
    useState({});
  const [
    createServiceRequestIframeLoaded,
    setCreateServiceRequestIframeLoaded,
  ] = useState(false);
  const [serviceRequestUrl, setServiceRequestUrl] = useState('');
  const [serviceRequestLoaded, setServiceRequestLoaded] =
    useState(false);
  const [selectedServiceRequestId, setSelectedServiceRequestId] =
    useState(null);
  const [srListLoaded, setSrListLoaded] = useState(false);

  /** UI State */
  const [selectedClinic, setSelectedClinic] = useState(undefined);
  const [selectedAccessory, setSelectedAccessory] = useState();
  const [selectedDevice, setSelectedDevice] = useState(undefined);
  const [selectedExpandedData, setSelectedExpandedData] =
    useState(undefined);
  const [rcRemoteModuleKey, setRcRemoteModuleKey] = useState(0);
  const [pageLevelError, setPageLevelError] = useState(false);
  const [mcpError, setMcpError] = useState(false);
  const [reportIsSubmitted, setReportIsSubmitted] = useState(false);

  /** Notification & Alert State */
  const [notiAlertConfig, setNotiAlertConfig] = useState(
    getNotiAlertConfig(
      cookieService.getCookie('notificationShown') === 'true',
      `${labels[deviceRegInProgressLabel]}\n${labels['labels.common.alert.deviceRequestSubmitted']}`,
    ),
  );

  /** Invite Modal State */
  const [inviteData, setInviteData] = useState({
    type: '',
    cochlearId: '',
    email: '',
    preferredLanguage: '',
    countryCode: '',
    resend: false,
  });
  const [inviteSendingInProgress, setInviteSendingInProgress] =
    useState(false);

  /** Refs */
  const selectedServiceRequestIframe = useRef();
  const createServiceRequestIframe = useRef();

  let loadedIds = [];

  /** Navigation handlers */
  const handleEditAccount = (carerId = null) => {
    history.push({
      pathname: `/${country}/${lang}/${routes.dpx.editPatient}`,
      search: `?patientId=${patientId}${
        carerId ? `&carerId=${carerId}` : ''
      }`,
    });
  };

  const handleCreateCarer = () => {
    history.push({
      pathname: `/${country}/${lang}/${routes.dpx.addCarer}`,
      search: `?patientId=${patientId}`,
    });
  };

  const handleAddAssociatedClinic = () => {
    setShowSelectSurgeryCentreModal(true);
  };

  /** Clinic search and selection */
  const handleSearchClinic = async (searchParam) => {
    const countryCode = searchParam.country || userCountry;
    const clinics = await providerService.searchClinics(
      countryCode,
      searchParam.clinic,
    );
    if (clinics.providers && clinics.providers.length > 0) {
      clinics.providers.forEach((provider) => {
        provider.address = getMergedAddress(provider);
      });
    }
    return clinics.providers;
  };

  const handleSelectClinic = (action, params) => {
    const selectedClinicData = {
      clinicId: params.data.id,
      name: params.data.name,
      address: params.data.address,
      isClinicAssociated: false,
      organisationType: params.data.organisationType,
    };
    const isClinicAssociated = associatedClinicData?.values.findIndex(
      (clinic) =>
        clinic.clinicId.value === selectedClinicData?.clinicId,
    );
    setSelectedClinic({
      ...selectedClinicData,
      isClinicAssociated: isClinicAssociated !== -1,
    });
  };

  /** Modal Handlers */
  const getAssociatedAccessories = (rawEquipmentData) => {
    if (!rawEquipmentData?.devices) {
      return [];
    }

    const today = new Date();

    // Eligibility: must be an accessory, under warranty, and have
    // "Create service request" enabled. isNewServiceRequestEnabled is set
    // false by the backend when there is an open incident, so this single
    // flag already satisfies the AC1 serviceable + no-open-incidents rules.
    return rawEquipmentData.devices.filter(
      (d) =>
        d.productType === 'Accessory' &&
        d.warrantyExpiration &&
        new Date(d.warrantyExpiration) > today &&
        d.isNewServiceRequestEnabled === true,
    );
  };

  // Runs an SR-flow action, first ensuring MCP's active clinic is synced. When
  // the session is live but the clinic hasn't been synced yet, the action is
  // deferred until ClinicSyncFrame confirms the switch; otherwise it runs now.
  const runWithClinicSync = (action) => {
    if (shouldRedirectForClinicSync(providerId)) {
      setPendingClinicSyncAction(() => action);
    } else {
      action();
    }
  };

  const displayModalCreateSR = (Id, deviceData) => {
    const rawEquipmentData = rawEquipmentListRef.current;
    const associatedAccessories =
      getAssociatedAccessories(rawEquipmentData);

    // Enhance deviceData with productSubFamily and accessories
    const rawDevice = rawEquipmentData?.devices?.find(
      (d) => d.deviceId === deviceData.deviceId,
    );

    const smartSyncScenario = computeSmartSyncScenario(
      rawDevice,
      rawEquipmentData?.devices,
    );

    const enhancedDeviceData = {
      ...deviceData,
      productSubFamily: rawDevice?.productSubFamily,
      smartSyncCompatibility: rawDevice?.smartSyncCompatibility,
      smartSyncScenario,
      modelGroup: rawDevice?.modelGroup,
      associatedAccessories,
    };

    if (useModuleFederationSR) {
      // End of Service check: if device is past its end of service date,
      // show EOS message instead of navigating to SR Portal
      if (
        rawDevice?.endOfServiceDate &&
        new Date() > new Date(rawDevice.endOfServiceDate)
      ) {
        setShowEndOfServiceModal(true);
        return;
      }

      const formattedDeviceData = formatDeviceDataForSR(
        enhancedDeviceData,
        Id,
      );
      const formattedPatientDetails = formatPatientDetailsForSR(
        patientDetailsRef.current,
      );

      // pass the current provider to SR Portal along with all patient-associated clinics
      const formattedClinicDetails = formatClinicDetailsForSR(
        currentProvider,
        associatedClinicDataRef.current,
      );
      // const formattedClinicDetails = formatClinicDetailsForSR(
      //   associatedClinicDataRef.current,
      //   providerCountryCode,
      //   providerId,
      // );

      history.push({
        pathname: `/${country}/${lang}/${routes.dpx.createServiceRequest}`,
        search: `?patientId=${patientId}`,
        state: {
          patientId,
          deviceData: formattedDeviceData,
          patientDetails: formattedPatientDetails,
          clinicDetails: formattedClinicDetails,
          countryCode: providerCountryCode,
          returnPath: `${location.pathname}${location.search}`,
        },
      });
      return;
    }

    // Legacy MCP iframe needs MCP's active clinic synced. If the session is
    // live but the clinic hasn't been synced, switch it in place (via
    // ClinicSyncFrame) and open the SR flow once the sync is confirmed.
    runWithClinicSync(() => {
      setDeviceId(Id);
      setSelectedDevice(enhancedDeviceData);
      setShowCreateServiceRequestModal(true);
      setCreateServiceRequestIframeLoaded(false);
    });
  };

  const displayViewServiceRequestModal = (url) => {
    // Legacy MCP iframe needs MCP's active clinic synced. Switch it in place if
    // it hasn't happened yet, then open the modal once the sync is confirmed.
    runWithClinicSync(() => {
      setServiceRequestUrl(url);
      setShowViewServiceRequestModal(true);
      setServiceRequestLoaded(false);
    });
  };

  const getPatientRecordReturnPath = (tab) => {
    const searchParams = new URLSearchParams(location.search);

    if (tab) {
      searchParams.set('tab', tab);
    } else {
      searchParams.delete('tab');
    }

    const search = searchParams.toString();
    return `${location.pathname}${search ? `?${search}` : ''}`;
  };

  const navigateToViewServiceRequestPage = (
    serviceRequestId,
    serviceRequestNumber,
    returnPath = getPatientRecordReturnPath(),
  ) => {
    const formattedPatientDetails = formatPatientDetailsForSR(
      patientDetailsRef.current,
    );

    history.push({
      pathname: `/${country}/${lang}/${routes.dpx.viewServiceRequest}`,
      state: {
        patientId,
        patientDetails: formattedPatientDetails,
        serviceRequestId,
        serviceRequestNumber,
        returnPath,
        source: 'patient-details',
      },
    });
  };

  const loadReportIssueModal = async (rowData) => {
    setReportIsSubmitted(false);
    setSelectedExpandedData(undefined);
    setSelectedDevice(rowData);
    setShowReportIssueModal(true);
    await fetchExpandedData(rowData);
  };

  const toggleUpdateAccessoryModal = (value, accessoryInfo) => {
    setShowUpdateAccessoryModal(value);
    setSelectedAccessory(accessoryInfo);
  };

  const displayReturnLoanerModal = (rowData) => {
    setSelectedDevice(rowData);
    setShowReturnLoanerModal(true);
  };

  /** Equipment Operations */
  const addEquipementCallback = () => {
    if (
      rawPatientDetails &&
      rawPatientDetails.data &&
      rawPatientDetails.data.id
    ) {
      history.push({
        pathname: `/${country}/${lang}/${routes.dpx.deviceRegistration}`,
        state: {
          patientName: `${rawPatientDetails?.data?.firstName} ${rawPatientDetails?.data?.lastName}`,
          dob: rawPatientDetails?.data?.dateOfBirth,
          cochlearId: rawPatientDetails.data.id,
          hasExistingDevice: hasExistingDevices(rawPatientDetails),
        },
      });
    }
  };

  const filterSoundProcessor = () => {
    createFilterSoundProcessor(
      equipmentDataRef.current,
      setEquipmentData,
    );
  };

  const filterImplants = () => {
    createFilterImplants(equipmentDataRef.current, setEquipmentData);
  };

  const filterAccessories = () => {
    createFilterAccessories(
      equipmentDataRef.current,
      setEquipmentData,
    );
  };

  const resetFilters = () => {
    resetPreviousExpandedSections();
    setEquipmentData(equipmentDataRef.current);
  };

  /** Expanded Data Operations */
  const getExpandedData = async (assetId) => {
    const expandedData = await patientService.getDeviceExpandedData(
      providerId,
      patientId,
      assetId,
    );
    const formattedExpandedData = formatExpandedData(
      expandedData,
      labels,
    );

    setEquipmentData((prevEquipmentData) => {
      const updatedData = prevEquipmentData.map((equipment) => {
        if (equipment.assetId === assetId) {
          return { ...equipment, ...formattedExpandedData };
        }
        return equipment;
      });
      return updatedData;
    });

    setOriginalEquipmentData((prevOriginalData) => {
      const updatedData = prevOriginalData.map((equipment) => {
        if (equipment.assetId === assetId) {
          return { ...equipment, ...formattedExpandedData };
        }
        return equipment;
      });
      return updatedData;
    });

    expandedDataCacheRef.current.set(assetId, formattedExpandedData);
    setSelectedExpandedData(formattedExpandedData);

    if (!loadedIds.includes(assetId)) {
      loadedIds.push(assetId);
    }
  };

  const fetchExpandedData = async (rData) => {
    const assetID = rData.assetId;
    if (!loadedIds.includes(assetID)) {
      await getExpandedData(assetID);
    } else {
      const cachedExpandedData =
        expandedDataCacheRef.current.get(assetID);
      if (cachedExpandedData) {
        setSelectedExpandedData(cachedExpandedData);
      }
    }
    return null;
  };

  /** Quick Update Operations */
  const updateEarSide = async (earSide, assetToUpdateId) => {
    setRightPanelConfig({ ...rightPanelConfig, isLoading: true });
    const respose = await patientService.updateEarSide(
      earSide,
      assetToUpdateId,
      professionalId,
      patientId,
      providerId,
    );
    await refreshEquipmentEarSide();
    if (respose) {
      setNotiAlertConfig((prevConfig) => ({
        ...prevConfig,
        visible: true,
        type: 'success',
        message:
          labels[
            'labels.patientRecord.views.notification.success.earSide'
          ],
      }));
    } else {
      setNotiAlertConfig((prevConfig) => ({
        ...prevConfig,
        visible: true,
        type: 'error',
        message:
          labels[
            'labels.patientRecord.views.notification.error.earSide'
          ],
      }));
    }
  };

  const updateAccessory = async (success) => {
    window.scrollTo(0, 0);
    const tempNotiAlertConfig = { ...notiAlertConfig };
    tempNotiAlertConfig.dismissible = false;
    tempNotiAlertConfig.visible = true;

    if (success) {
      tempNotiAlertConfig.type = 'success';
      tempNotiAlertConfig.message =
        labels['labels.patientRecord.updateAccessoryModal.success'];
      await refreshEquipment();
    } else {
      const failureMessageTemplate =
        labels['labels.patientRecord.updateAccessoryModal.failure'];
      const fullName = `${rawPatientDetails?.data?.firstName} ${rawPatientDetails?.data?.lastName}`;
      const failureMessage = failureMessageTemplate.replace(
        '{{Name}}',
        fullName,
      );

      tempNotiAlertConfig.type = 'error';
      tempNotiAlertConfig.message = failureMessage;
    }
    setNotiAlertConfig(tempNotiAlertConfig);
  };

  const viewServiceRequestForRecipientDevice = (row) => {
    const srId = row?.serviceRequestId;
    const srNumber = row?.serviceRequestNumber;
    setSelectedServiceRequestId(srId);
    if (useModuleFederationSR) {
      navigateToViewServiceRequestPage(
        srId,
        srNumber,
        getPatientRecordReturnPath(),
      );
      return;
    }
    const tokens = [
      {
        key: `##___SERVICEREQUESTID___##`,
        value: srId,
      },
    ];
    displayViewServiceRequestModal(
      setIdpParam(
        `${replaceTokens(
          routes.mcp.viewServiceRequestWithoutDomain,
          tokens,
        )}${patientId}`,
      ),
    );
  };

  const returnLoanerDevice = async () => {
    if (!selectedDevice) return;
    setReturnLoanerSubmitting(true);
    const response = await returnLoaner(
      providerId,
      selectedDevice.assetId,
      patientId,
    );

    const success = response?.success;
    const alertMsg = success
      ? `${labels['labels.patientRecord.views.notification.success.returnLoaner']}`
      : `${labels['labels.patientRecord.views.notification.error.returnLoaner']}`;
    setNotiAlertConfig((prevConfig) => ({
      ...prevConfig,
      type: success ? 'success' : 'error',
      visible: true,
      message: alertMsg,
    }));

    setReturnLoanerSubmitting(false);
    setShowReturnLoanerModal(false);
    window.scrollTo(0, 0);

    if (success) {
      await refreshEquipment();
    }
  };

  /** Refresh Operations */
  const refreshEquipment = async () => {
    // need to reset loadedIds, because equipment will be refreshed
    loadedIds = [];
    setRightPanelConfig({ ...rightPanelConfig, isLoading: true });
    fetchEquipment(cacheControlHeader);
  };

  const refreshEquipmentEarSide = async () => {
    // need to reset loadedIds, because equipment will be refreshed
    loadedIds = [];
    await fetchEquipment(cacheControlHeader);
    fetchPatientDetails();
  };

  const refreshPatientDetails = async () => {
    const updatedPanelConfig = getUpdatedLeftPanelConfig(
      leftPanelConfig,
      'patientDetails',
      (view) => {
        const updatedView = { ...view };
        updatedView.isLoading = true;
        return updatedView;
      },
    );

    setLeftPanelConfig(updatedPanelConfig);
    fetchPatientDetails(cacheControlHeader);
  };

  const refreshCarerData = async () => {
    const associatedCarersView = getUpdatedLeftPanelConfig(
      leftPanelConfig,
      'associatedCarersView',
      (view) => {
        const updatedView = {
          ...view,
          isLoading: true,
        };
        return updatedView;
      },
    );

    setLeftPanelConfig(associatedCarersView);
    fetchPatientCarers(cacheControlHeader);
  };

  const refreshPatientData = async () => {
    setRawPatientDetails({});
    setMcpError(false);
    setRcRemoteModuleKey((prev) => prev + 1);
    await refreshPatientDetails();
    await refreshCarerData();

    const associatedClinicsView = getUpdatedLeftPanelConfig(
      leftPanelConfig,
      'associatedClinicsView',
      (view) => {
        const updatedView = {
          ...view,
          isLoading: true,
        };
        return updatedView;
      },
    );
    setLeftPanelConfig(associatedClinicsView);
    fetchPatientClinics(cacheControlHeader);
  };

  /** API Calls */
  const patientDetailsCall = async (customHeaders) => {
    const rawPatient = await patientService.getPatient(
      providerId,
      patientId,
      customHeaders,
    );
    if (rawPatient === null || rawPatient.data.errors) {
      dispatch(
        error(
          rawPatient?.status === 404
            ? initialRightPanelConfig.notFoundErrorConfig
            : true,
        ),
      );
      setPageLevelError(true);
    }
    return rawPatient;
  };

  const fetchPatientDetails = async (customHeaders) => {
    let rawPatient = await patientDetailsCall(customHeaders);

    if (!rawPatient) {
      return;
    }

    const { data } = rawPatient;

    if (
      (!data?.firstName || !(data?.personas ?? null)) &&
      !customHeaders
    ) {
      rawPatient = await patientDetailsCall(cacheControlHeader);
    }

    const formattedPatientDetails = patientData(
      rawPatient.data,
      rawPatient.errors,
      labels,
    );
    setRawPatientDetails(rawPatient);
    setPatientDetails(formattedPatientDetails);
    patientDetailsRef.current = formattedPatientDetails;

    const updatedPanelConfig = getUpdatedLeftPanelConfig(
      leftPanelConfig,
      'patientDetails',
      (view) => {
        const updatedView = { ...view };

        if (rawPatient?.data?.isUnderAge) {
          updatedView.read.elements = view.read.elements.filter(
            (x) => {
              if (rawPatient?.data?.accountStatus?.created) {
                return x.id !== 'phone' && x.id && x.id !== 'address';
              }

              return (
                x.id !== 'phone' &&
                x.id !== 'email' &&
                x.id !== 'accountDetails' &&
                x.id !== 'address'
              );
            },
          );
        }

        updatedView.isLoading = false;
        return updatedView;
      },
    );
    setLeftPanelConfig(updatedPanelConfig);
  };

  const fetchPatientCarers = async (customHeaders) => {
    const rawCarersList = await patientService.getPatientCarers(
      providerId,
      patientId,
      customHeaders,
    );
    setRawCarersDetails(rawCarersList);

    if (rawCarersList === null) {
      setCarersData(
        getGenericErrorData(labels, 'associatedCarersView'),
      );
      setLeftPanelConfig(
        getUpdatedLeftPanelConfig(
          leftPanelConfig,
          'associatedCarersView',
          injectViewErrorConfig,
        ),
      );
      return;
    }

    // Merge optimistic carer if the real data hasn't synced yet
    let mergedCarersList = rawCarersList;
    if (optimisticCarerRef.current) {
      const { id, firstName, lastName, email } =
        optimisticCarerRef.current.data;

      let isSynced;
      if (!id) {
        // New carer (add flow) — synced when it appears in the list
        isSynced = rawCarersList.some(
          ({ data }) =>
            data.firstName?.toLowerCase() ===
              firstName?.toLowerCase() &&
            data.lastName?.toLowerCase() ===
              lastName?.toLowerCase() &&
            data.email === email,
        );
      } else {
        // Existing carer (edit flow) — synced when API reflects updated values
        const match = rawCarersList.find(
          ({ data }) => data.id === id,
        );
        isSynced =
          match &&
          match.data.firstName?.toLowerCase() ===
            firstName?.toLowerCase() &&
          match.data.lastName?.toLowerCase() ===
            lastName?.toLowerCase() &&
          match.data.email === email;
      }

      if (!isSynced) {
        if (id) {
          // Edit flow: replace the stale entry with optimistic data
          mergedCarersList = rawCarersList.map((carer) =>
            carer.data.id === id ? optimisticCarerRef.current : carer,
          );
        } else {
          // Add flow: prepend the optimistic entry
          mergedCarersList = [
            optimisticCarerRef.current,
            ...rawCarersList,
          ];
        }
      } else {
        optimisticCarerRef.current = null;
        sessionStorage.removeItem(`dpx_optimisticCarer_${patientId}`);
      }
    }

    const isEmpty = !mergedCarersList.length;
    const formattedPatientCarersData = isEmpty
      ? getGenericEmptyListData(labels, 'associatedCarersView')
      : formatPatientCarersData(mergedCarersList, labels);

    setCarersData(formattedPatientCarersData);

    const updatedPanelConfig = getUpdatedLeftPanelConfig(
      leftPanelConfig,
      'associatedCarersView',
      (view) => {
        const updatedView = {
          ...view,
          isLoading: false,
        };

        if (isEmpty) {
          updatedView.read.elements = genericEmptyListElementsConfig;
        } else {
          updatedView.read.elements = getCarerReadElementsConfig(
            initialLeftPanelConfig,
          );
        }
        return updatedView;
      },
    );
    setLeftPanelConfig(updatedPanelConfig);
  };

  const associationMessages = [
    labels['labels.patientRecord.associationSpinner.message1'] ||
      'Association of patient to the clinic may take up to 1 minute ...',
    labels['labels.patientRecord.associationSpinner.message2'] ||
      'Authorising you to associate the patient to the clinic ...',
    labels['labels.patientRecord.associationSpinner.message3'] ||
      'Associating the patient to the clinic ...',
  ];

  const startAssociationPolling = (newClinicId) => {
    setShowAssociationSpinner(true);
    isPollActiveRef.current = true;
    let attempts = 0;
    const maxAttempts = 12;

    const finish = async () => {
      setShowAssociationSpinner(false);
      setHeadingDetails((prev) => ({
        ...prev,
        alertConfig: {
          ...prev.alertConfig,
          type: 'success',
          visible: true,
          title: '',
          message: `${labels['labels.patientRecord.views.associatedClinicsView.associatingClinic.success']}`,
          onClose: () => {},
        },
      }));
      fetchPatientClinics({ 'Cache-Control': 'no-cache' });
    };

    const poll = async () => {
      if (!isPollActiveRef.current) return;
      attempts += 1;
      const providers =
        await patientService.getPatientAssociatedClinics(
          providerId,
          patientId,
          { 'Cache-Control': 'no-cache' },
        );
      if (!isPollActiveRef.current) return;
      const found = providers?.some((p) => p.id === newClinicId);
      if (found || attempts >= maxAttempts) {
        finish();
        return;
      }
      navigationPollRef.current = setTimeout(poll, 5000);
    };

    poll();
  };

  useEffect(() => {
    return () => {
      isPollActiveRef.current = false;
      if (navigationPollRef.current) {
        clearTimeout(navigationPollRef.current);
      }
    };
  }, []);

  const fetchPatientClinics = async (customHeaders) => {
    const rawProvidersList =
      await patientService.getPatientAssociatedClinics(
        providerId,
        patientId,
        customHeaders,
      );

    if (rawProvidersList === null) {
      const errorClinicData = getGenericErrorData(
        labels,
        'associatedClinicsView',
      );
      setAssociatedClinicData(errorClinicData);
      associatedClinicDataRef.current = errorClinicData;
      setLeftPanelConfig(
        getUpdatedLeftPanelConfig(
          leftPanelConfig,
          'associatedClinicsView',
          injectViewErrorConfig,
        ),
      );
      return;
    }

    const isEmpty = !rawProvidersList.length;

    const formattedClinicData = isEmpty
      ? getGenericEmptyListData(labels, 'associatedClinicsView')
      : formatPatientClinicData(rawProvidersList);

    setAssociatedClinicData(formattedClinicData);
    associatedClinicDataRef.current = formattedClinicData;

    const updatedPanelConfig = getUpdatedLeftPanelConfig(
      leftPanelConfig,
      'associatedClinicsView',
      (view) => {
        const updatedView = {
          ...view,
          isLoading: false,
        };

        if (isEmpty) {
          updatedView.read.elements = genericEmptyListElementsConfig;
        }
        return updatedView;
      },
    );
    setLeftPanelConfig(updatedPanelConfig);
  };

  const fetchEquipment = async (customHeaders) => {
    try {
      const equipmentListResponse =
        await patientService.getPatientDevices(
          providerId,
          professional.professionalId,
          patientId,
          customHeaders,
          providerCountryCode,
        );

      // Store the raw equipment list for access by modals (both state and ref)
      setRawEquipmentList(equipmentListResponse);
      rawEquipmentListRef.current = equipmentListResponse;

      if (equipmentListResponse === null) {
        setRightPanelConfig((prevConfig) => ({
          ...prevConfig,
          isLoading: false,
          hasError: true,
          serviceRequestConfig: {
            ...prevConfig.serviceRequestConfig,
            notApplicable: true,
          },
        }));
        return;
      }

      const formattedEquipmentData = formatPatientDeviceData(
        equipmentListResponse,
        labels,
      );
      if (formattedEquipmentData.data.length === 0) {
        // Empty equipment list
        setRightPanelConfig((prevConfig) => ({
          ...prevConfig,
          isLoading: false,
          isEmpty: true,
          serviceRequestConfig: {
            ...prevConfig.serviceRequestConfig,
            notApplicable: true,
          },
        }));
      } else {
        setOriginalEquipmentData(formattedEquipmentData.data);
        setEquipmentData(formattedEquipmentData.data);

        if (formattedEquipmentData.contextMenuMap) {
          const tableConfigTemp = { ...rightPanelConfig.tableConfig };
          tableConfigTemp.columns[4].format.collapsed.map =
            formattedEquipmentData.contextMenuMap;
          tableConfigTemp.tableConfigRight.columns[4].format.collapsed.map =
            formattedEquipmentData.contextMenuMap;
          tableConfigTemp.tableConfigLeft.columns[4].format.collapsed.map =
            formattedEquipmentData.contextMenuMap;
          tableConfigTemp.tableConfigUnknown.columns[4].format.collapsed.map =
            formattedEquipmentData.contextMenuMap;

          setRightPanelConfig({
            ...rightPanelConfig,
            tableConfig: tableConfigTemp,
          });
        }
        // set isLoading to false if API call is successful
        setRightPanelConfig({
          ...rightPanelConfig,
          isLoading: false,
          isEmpty: false,
        });
      }
    } catch (e) {
      setRightPanelConfig({
        ...rightPanelConfig,
        isLoading: false,
        hasError: true,
      });
    }
  };

  /** Account Invite Handlers */
  const onInviteLinkClicked = (id, type) => {
    const personalDetails =
      type === 'PATIENT'
        ? patientDetails
        : carersData?.values.find((cr) => cr.accountId.value === id);

    const almRequestBody = createAlmRequestBody(
      id,
      type,
      personalDetails,
      professionalId,
      providerId,
      country,
      lang,
      type === 'CARER' ? patientDetails.accountId.value : null,
    );

    setInviteData({
      ...almRequestBody,
    });
    setShowInviteModal(true);
    setHeadingDetails({
      ...headingDetails,
      title: {
        ...headingDetails.title,
        content: patientTitle,
      },
    });
  };

  const onInviteModalClose = async (send) => {
    if (send) {
      setInviteSendingInProgress(true);

      if (inviteData.emailUpdated) {
        const requestBody = {
          email: inviteData.email,
        };
        if (inviteData.accountFor.toLowerCase() === 'patient') {
          await patientService.updatePatient(
            requestBody,
            providerId,
            patientId,
          );
        }

        if (inviteData.accountFor.toLowerCase() === 'carer') {
          await carerService.updateCarer(
            requestBody,
            providerId,
            inviteData.relatedDependent,
            inviteData.cochlearId,
          );
        }
      }

      const success = await patientService.sendALM(
        inviteData,
        providerId,
      );
      const alertMsg = success
        ? `${labels['labels.patientRecord.heading.alert.title.invite.success']}`
        : `${labels['labels.patientRecord.heading.alert.title.invite.error']}`;
      setNotiAlertConfig((prevConfig) => ({
        ...prevConfig,
        type: success ? 'success' : 'error',
        visible: true,
        message: alertMsg,
      }));
    }
    setShowInviteModal(false);
    setInviteSendingInProgress(false);

    if (send && !inviteData.resend) {
      if (inviteData.type === 'PATIENT') {
        refreshPatientDetails();
      } else {
        refreshCarerData();
      }
    }
  };

  /** Panel Configuration State */
  const initialLeftPanelConfig = getLeftPanelConfig();
  const [leftPanelConfig, setLeftPanelConfig] = useState({
    ...panelConfigMapper(
      initialLeftPanelConfig,
      professional,
      handleEditAccount,
      handleCreateCarer,
      handleAddAssociatedClinic,
    ),
    routerLocation: useLocation,
  });

  const initialRightPanelConfig = getRightPanelConfig(
    labels,
    selectedRecipientCId,
    patientId,
    displayModalCreateSR,
    fetchExpandedData,
    addEquipementCallback,
    filterSoundProcessor,
    filterAccessories,
    filterImplants,
    resetFilters,
    loadReportIssueModal,
    updateEarSide,
    toggleUpdateAccessoryModal,
    refreshEquipment,
    viewServiceRequestForRecipientDevice,
    displayReturnLoanerModal,
  );
  const [rightPanelConfig, setRightPanelConfig] = useState(
    initialRightPanelConfig,
  );

  /** Derived State */
  const age = getAge(patientDetails?.dateOfBirth?.value);
  const patientTitle = `${getFullName(patientDetails)} ${getDobString(
    age,
  )}`;
  const createServiceRequestModalTitle =
    labels[
      'labels.patientRecord.tabs.equipments.dataTable.options.createServiceRequest'
    ];

  const defaultHeadingDetails = {
    title: {
      content: patientTitle,
    },
    buttonConfig: {
      variant: 'tinted',
      icon: 'refresh',
      onClick: refreshPatientData,
      size: 'small',
      analytics: 'refresh_view_patient',
    },
  };
  const [headingDetails, setHeadingDetails] = useState(
    defaultHeadingDetails,
  );

  /** Config Setup */
  const clinicSelectorConfig = getFindClinicModalConfig(
    userCountry,
    labels,
  );
  clinicSelectorConfig.searchButton.onClick = handleSearchClinic;
  clinicSelectorConfig.tableConfig.events.onRowClick =
    handleSelectClinic;

  const leftPanelData = {
    adaptiveForms: {
      patientDetailsView: patientDetails,
      carersData,
      associatedClinicData,
    },
    errorModes: errorModeConfig,
  };

  /** USE EFFECTS */
  useEffect(() => {
    equipmentDataRef.current = originalEquipmentData;
  }, [originalEquipmentData]);

  useEffect(() => {
    dispatch(error(false));
  }, []);

  useEffect(() => {
    if (selectedClinic) {
      setShowAuthorizationAssociateClinicModal(true);
    } else {
      setShowAuthorizationAssociateClinicModal(false);
    }
    if (!showAuthorizationAssociateClinicModal) {
      const element = document.querySelector('body');
      element.style = {};
    }
  }, [selectedClinic, showAuthorizationAssociateClinicModal]);

  useEffect(() => {
    if (cookieService.getCookie('notificationShown') === 'true') {
      const handleBackButton = () => {
        window.location.reload();
      };
      window.addEventListener('popstate', handleBackButton);
      return () => {
        window.removeEventListener('popstate', handleBackButton);
      };
    }
    return undefined;
  }, []);

  useEffect(() => {
    setTimeout(() => {
      cookieService.setCookie('notificationShown', 'false');
    }, 1000);
  }, []);

  useEffect(() => {
    let pollingIntervalId = null;

    const initCarers = async () => {
      if (optimisticCarerRef.current) {
        setCarersData(
          formatPatientCarersData(
            [optimisticCarerRef.current],
            labels,
          ),
        );
        setLeftPanelConfig(
          getUpdatedLeftPanelConfig(
            leftPanelConfig,
            'associatedCarersView',
            (view) => ({
              ...view,
              isLoading: false,
              read: {
                ...view.read,
                elements: getCarerReadElementsConfig(
                  initialLeftPanelConfig,
                ),
              },
            }),
          ),
        );
      }

      await fetchPatientCarers(
        optimisticCarerRef.current ? cacheControlHeader : undefined,
      );

      // If the optimistic carer is still unsynced after the initial
      // fetch, poll every 10s (up to 90s) until the real API returns it.
      if (optimisticCarerRef.current) {
        const POLL_INTERVAL_MS = 10 * 1000;
        const MAX_POLLS = 9;
        let pollCount = 0;

        pollingIntervalId = setInterval(async () => {
          pollCount += 1;
          if (!optimisticCarerRef.current || pollCount >= MAX_POLLS) {
            clearInterval(pollingIntervalId);
            return;
          }
          await fetchPatientCarers(cacheControlHeader);
        }, POLL_INTERVAL_MS);
      }
    };

    fetchPatientDetails();
    if (!isDeceasedState) {
      initCarers();
      fetchPatientClinics();
      fetchEquipment();
    }

    return () => {
      if (pollingIntervalId) clearInterval(pollingIntervalId);
    };
  }, []);

  useEffect(() => {
    if (!document || !patientDetails?.lastName) return true;

    setHeadingDetails({
      ...headingDetails,
      title: {
        ...headingDetails.title,
        content: patientTitle,
      },
    });

    const globalPatientListener = (e) => {
      const targetId = e.target.id;
      if (targetId.indexOf('pt_invite_link_') === 0) {
        e.preventDefault();
        onInviteLinkClicked(e.target.dataset.id, 'PATIENT');
      }
    };

    document.addEventListener('click', globalPatientListener);
    return () =>
      document.removeEventListener('click', globalPatientListener);
  }, [patientDetails]);

  useEffect(() => {
    if (!document || !carersData || !patientDetails) return true;

    const globalCarerListener = (e) => {
      const targetId = e.target.id;
      const carerId = e.target.dataset.id;
      const isValidCarerId = carerId && carerId !== 'null';
      if (targetId.indexOf('cr_invite_link_') === 0) {
        e.preventDefault();
        if (isValidCarerId) {
          onInviteLinkClicked(carerId, 'CARER');
        }
      } else if (targetId.indexOf('cr_view_') === 0) {
        e.preventDefault();
        if (isValidCarerId) {
          handleEditAccount(carerId);
        }
      }
    };

    document.addEventListener('click', globalCarerListener);
    return () =>
      document.removeEventListener('click', globalCarerListener);
  }, [carersData, patientDetails]);

  useEffect(() => {
    const handleMessage = (e) => {
      if (e.origin !== routes.mcp.domain) return;
      if (typeof e.data === 'object') {
        if ('modalEvent' in e.data) {
          switch (e.data.modalEvent.target) {
            case 'serviceRequest':
              if (e.data.modalEvent.type === 'open') {
                displayViewServiceRequestModal(
                  e.data.modalEvent.payload.url,
                );
              }
              break;

            case 'createServiceRequest':
              if (e.data.modalEvent.type === 'close') {
                setShowCreateServiceRequestModal(false);
              }
              break;
            default:
              break;
          }
        }
        if ('pageEvent' in e.data) {
          const mcpPageHeigth = e.data.pageEvent.payload?.height;
          switch (e.data.pageEvent.type) {
            case 'pageLoaded':
              if (e.data.pageEvent.target === 'serviceRequestList') {
                setSrListLoaded(true);
              }
              if (
                e.data.pageEvent.target === 'selectedServiceRequest'
              ) {
                if (
                  selectedServiceRequestIframe &&
                  selectedServiceRequestIframe.current
                ) {
                  selectedServiceRequestIframe.current.style.visibility =
                    'visible';
                  selectedServiceRequestIframe.current.style.height =
                    mcpPageHeigth === 0
                      ? '1800px'
                      : `${mcpPageHeigth + 100}px`;
                  setServiceRequestLoaded(true);
                }
              }
              if (
                e.data.pageEvent.target === 'createServiceRequest'
              ) {
                if (
                  createServiceRequestIframe &&
                  createServiceRequestIframe.current
                ) {
                  createServiceRequestIframe.current.style.visibility =
                    'visible';
                  createServiceRequestIframe.current.style.height =
                    mcpPageHeigth === 0
                      ? '300px'
                      : `${mcpPageHeigth}px`;
                  setCreateServiceRequestIframeLoaded(true);
                }
              }
              break;
            case 'serviceError':
              setMcpError(true);
              break;
            default:
              break;
          }
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    const handleMessage = (e) => {
      if (e.origin !== routes.mcp.domain) return;
      if (typeof e.data === 'object' && 'pageEvent' in e.data) {
        const { type, target } = e.data.pageEvent;
        if (type === 'srCreated' && target === 'equipment') {
          refreshEquipment();
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    setCreateServiceRequestUrl(
      setIdpParam(
        replaceTokens(routes.mcp.createServiceRequest, arrayTokens) +
          deviceId,
      ),
    );
  }, [deviceId]);

  useEffect(() => {
    if (srListLoaded) {
      const newRhsConfig = { ...rightPanelConfig };
      newRhsConfig.serviceRequestConfig.isLoading = false;
      setRightPanelConfig(newRhsConfig);
    }
  }, [srListLoaded]);

  useEffect(() => {
    const newRhsConfig = { ...rightPanelConfig };
    if (mcpError) {
      newRhsConfig.serviceRequestConfig.hasError = true;
    } else {
      newRhsConfig.serviceRequestConfig.hasError = false;
    }
    setRightPanelConfig(newRhsConfig);

    if (mcpError) {
      const reloadTimeoutId = setTimeout(() => {
        const cfg = { ...rightPanelConfig };
        cfg.serviceRequestConfig.hasError = false;
        setMcpError(false);
        setRightPanelConfig(cfg);
      }, 30000);

      return () => clearTimeout(reloadTimeoutId);
    }

    return () => {};
  }, [mcpError]);

  useEffect(() => {
    if (Object.keys(rawPatientDetails).length !== 0) {
      const tempRhsConfig = rightPanelConfig;
      tempRhsConfig.taskBarConfig.button.onClick =
        addEquipementCallback;
      tempRhsConfig.emptyEquipment.button.onClick =
        addEquipementCallback;

      if (
        rawPatientDetails.data?.personas?.length > 0 &&
        rawPatientDetails.data?.personas.indexOf('Recipient') === -1
      ) {
        tempRhsConfig.serviceRequestConfig.notApplicable = true;
        tempRhsConfig.serviceRequestConfig.button = {
          onClick: addEquipementCallback,
          analytics: 'add_device_service_requests_view_patient',
        };
      } else {
        tempRhsConfig.serviceRequestConfig.notApplicable = false;
        tempRhsConfig.serviceRequestConfig.hasError = mcpError;
        delete tempRhsConfig.serviceRequestConfig.button;
      }

      setRightPanelConfig(tempRhsConfig);
    }
  }, [rawPatientDetails]);

  useEffect(() => {
    if (patientDetails && rawPatientDetails) {
      if (rawPatientDetails.data?.isDeceased) {
        setHeadingDetails(
          getPatientDeceasedConfig(
            headingDetails,
            patientTitle,
            labels,
            country,
            lang,
          ),
        );
      } else {
        setHeadingDetails({
          ...headingDetails,
          title: {
            ...headingDetails.title,
            content: patientTitle,
          },
        });
      }
    }
  }, [patientDetails, rawPatientDetails]);

  useEffect(() => {
    const isDeviceSuccess = notifications.some(
      (item) =>
        item.details.entity === 'Device' &&
        item.details.status === 'processed' &&
        new Date() - new Date(item.lastUpdated) < 60000,
    );
    if (isDeviceSuccess) {
      setNotiAlertConfig((prevConfig) => ({
        ...prevConfig,
        visible: false,
      }));
      refreshEquipment();
      // patient persona should be changed from Pending to Recipient as a result of the device registration
      refreshPatientData();
    }
  }, [notifications]);

  return (
    !pageLevelError && (
      <Feature name="featureEnabler/viewPatient">
        {(isEnabled) =>
          isEnabled ? (
            <PatientRecordContext.Provider
              value={{
                showAuthorizationAssociateClinicModal,
                selectedClinic,
                setSelectedClinic,
                getFullName,
                setShowSelectSurgeryCentreModal,
                patientId,
                providerId,
                headingDetails,
                setHeadingDetails,
                leftPanelConfig,
                getUpdatedLeftPanelConfig,
                setLeftPanelConfig,
                fetchPatientClinics,
                cacheControlHeader,
                showViewServiceRequestModal,
                setShowViewServiceRequestModal,
                serviceRequestLoaded,
                setServiceRequestLoaded,
                serviceRequestUrl,
                selectedServiceRequestId,
                selectedServiceRequestIframe,
                createServiceRequestModalTitle,
                showCreateServiceRequestModal,
                setShowCreateServiceRequestModal,
                showEndOfServiceModal,
                setShowEndOfServiceModal,
                createServiceRequestIframeLoaded,
                createServiceRequestUrl,
                createServiceRequestIframe,
                deviceId,
                showReportIssueModal,
                setShowReportIssueModal,
                showReturnLoanerModal,
                setShowReturnLoanerModal,
                professional,
                reportIsSubmitted,
                setReportIsSubmitted,
                age,
                selectedDevice,
                selectedExpandedData,
                isDistributor,
                labels,
                providerRegion,
                useModuleFederationSR,
                displayViewServiceRequestModal,
                setSelectedServiceRequestId,
                rawEquipmentList,
                patientDetails,
                associatedClinicData,
                providerCountryCode,
                currentProvider,
                startAssociationPolling,
              }}
            >
              <FindClinicModal
                {...clinicSelectorConfig}
                show={showSelectSurgeryCentreModal}
                onHide={() => setShowSelectSurgeryCentreModal(false)}
                labels={labels}
              />
              <AuthorizationAssociateClinicModal />

              {pendingClinicSyncAction && (
                <ClinicSyncFrame
                  cochlearId={providerId}
                  onSynced={() => {
                    const action = pendingClinicSyncAction;
                    setPendingClinicSyncAction(null);
                    action();
                  }}
                />
              )}

              {showAssociationSpinner ? (
                <AssociationSpinner messages={associationMessages} />
              ) : (
                <>
                  <Feature name="featureEnabler/serviceRequest">
                    <ViewServiceRequestModal />
                  </Feature>
                  <CreateServiceRequestModal />
                  <EndOfServiceModal />
                  <ReportIssueModal />
                  <AccountInviteModal
                    rendering={rendering}
                    providerId={providerId}
                    inviteData={inviteData}
                    setInviteData={setInviteData}
                    showInviteModal={showInviteModal}
                    onInviteModalClose={onInviteModalClose}
                    inviteSendingInProgress={inviteSendingInProgress}
                    setInviteSendingInProgress={
                      setInviteSendingInProgress
                    }
                    loggedInCountryCode={country}
                  />
                  <UpdateAccessoryModal
                    rendering={rendering}
                    showUpdateAccessoryModal={
                      showUpdateAccessoryModal
                    }
                    selectedAccessory={selectedAccessory}
                    setShowUpdateAccessoryModal={
                      setShowUpdateAccessoryModal
                    }
                    country={country}
                    uDetails={professional}
                    providerId={providerId}
                    patientId={patientId}
                    updateAccessory={updateAccessory}
                  />

                  <ReturnLoanerModal
                    labels={labels}
                    onClose={() => setShowReturnLoanerModal(false)}
                    onSubmit={returnLoanerDevice}
                    visible={showReturnLoanerModal}
                    isSubmitting={returnLoanerSubmitting}
                  />

                  <div className="ccl__container">
                    <div className="ccl__main-patient">
                      <TopHeading headingDetails={headingDetails} />
                      <NotiAlert config={notiAlertConfig} />
                      {headingDetails.errorConfig?.isDeceased ? (
                        <DeceasedView
                          config={headingDetails.errorConfig}
                        />
                      ) : (
                        <Row>
                          <LeftPanel
                            config={leftPanelConfig}
                            data={leftPanelData}
                            labels={labels}
                          />
                          <RightPanel
                            config={rightPanelConfig}
                            data={equipmentData}
                            country={country}
                            labels={labels}
                            lang={lang}
                            leftData={filterSoundProcessorsSummary(
                              originalEquipmentData.filter(
                                (data) =>
                                  data.earLeft &&
                                  data.icon &&
                                  data.device?.type,
                              ),
                            )}
                            rightData={filterSoundProcessorsSummary(
                              originalEquipmentData.filter(
                                (data) =>
                                  !data.earLeft &&
                                  data.icon &&
                                  data.device?.type,
                              ),
                            )}
                            unknownData={filterUnknownDataSummary(
                              originalEquipmentData.filter(
                                (data) => data.isUnknownEarSIde,
                              ),
                            )}
                            rcData={{
                              patientId,
                              providerId,
                              accessTokenCookieName:
                                ACCESS_TOKEN_COOKIE,
                            }}
                            rcRemoteModuleKey={rcRemoteModuleKey}
                            useModuleFederationSR={
                              useModuleFederationSR
                            }
                            providerRegion={providerRegion}
                            patientId={patientId}
                          />
                        </Row>
                      )}
                    </div>
                  </div>
                </>
              )}
            </PatientRecordContext.Provider>
          ) : (
            <DisabledFeature
              title={labels['labels.common.patientRecord']}
              message={
                labels['labels.common.featureFlag.disabledText']
              }
            />
          )
        }
      </Feature>
    )
  );
};

export default PatientRecord;
